import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  CreditCard,
  Plus,
  Trash2,
  Edit,
  Check,
  X,
  Eye,
  EyeOff,
  Building2,
  Store,
  Users,
  Calendar,
  Zap,
  Crown,
  Gift,
  Settings,
  ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PlanManagementPage = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('builder');
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [builders, setBuilders] = useState([]);
  const [salesCompanies, setSalesCompanies] = useState([]);
  
  // Modals
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [assigningPlan, setAssigningPlan] = useState(null);
  
  // Form state
  const [planForm, setPlanForm] = useState({
    name: '',
    description: '',
    price_monthly: '',
    price_quarterly: '',
    price_yearly: '',
    billing_intervals: ['monthly'],
    trial_days: 0,
    category: 'builder',
    is_hidden: false,
    features: '',
    max_users: '',
    max_leads: '',
    max_customers: '',
    max_projects: '',
    custom_branding: false,
    api_access: false,
    priority_support: false,
    badge_text: '',
    sort_order: 0
  });
  
  const [assignForm, setAssignForm] = useState({
    target_id: '',
    billing_interval: 'monthly',
    start_trial: false
  });
  
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchPlans();
    fetchSubscriptions();
    fetchBuilders();
    fetchSalesCompanies();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/plans/?include_hidden=true`, { headers });
      setPlans(response.data);
    } catch (error) {
      console.error('Failed to fetch plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscriptions = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/plans/subscriptions/all`, { headers });
      setSubscriptions(response.data.subscriptions || []);
    } catch (error) {
      console.error('Failed to fetch subscriptions:', error);
    }
  };

  const fetchBuilders = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/builders/`, { headers });
      setBuilders(response.data);
    } catch (error) {
      console.error('Failed to fetch builders:', error);
    }
  };

  const fetchSalesCompanies = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/sales-companies/`, { headers });
      setSalesCompanies(response.data);
    } catch (error) {
      console.error('Failed to fetch sales companies:', error);
    }
  };

  const resetPlanForm = () => {
    setPlanForm({
      name: '',
      description: '',
      price_monthly: '',
      price_quarterly: '',
      price_yearly: '',
      billing_intervals: ['monthly'],
      trial_days: 0,
      category: activeTab,
      is_hidden: false,
      features: '',
      max_users: '',
      max_leads: '',
      max_customers: '',
      max_projects: '',
      custom_branding: false,
      api_access: false,
      priority_support: false,
      badge_text: '',
      sort_order: 0
    });
    setEditingPlan(null);
  };

  const openEditModal = (plan) => {
    setEditingPlan(plan);
    setPlanForm({
      name: plan.name,
      description: plan.description || '',
      price_monthly: plan.price_monthly?.toString() || '',
      price_quarterly: plan.price_quarterly?.toString() || '',
      price_yearly: plan.price_yearly?.toString() || '',
      billing_intervals: plan.billing_intervals || ['monthly'],
      trial_days: plan.trial_days || 0,
      category: plan.category || 'builder',
      is_hidden: plan.is_hidden || false,
      features: (plan.features || []).join('\n'),
      max_users: plan.max_users?.toString() || '',
      max_leads: plan.max_leads?.toString() || '',
      max_customers: plan.max_customers?.toString() || '',
      max_projects: plan.max_projects?.toString() || '',
      custom_branding: plan.custom_branding || false,
      api_access: plan.api_access || false,
      priority_support: plan.priority_support || false,
      badge_text: plan.badge_text || '',
      sort_order: plan.sort_order || 0
    });
    setShowPlanModal(true);
  };

  const savePlan = async () => {
    try {
      const payload = {
        name: planForm.name,
        description: planForm.description || null,
        price_monthly: parseFloat(planForm.price_monthly) || 0,
        price_quarterly: planForm.price_quarterly ? parseFloat(planForm.price_quarterly) : null,
        price_yearly: planForm.price_yearly ? parseFloat(planForm.price_yearly) : null,
        billing_intervals: planForm.billing_intervals,
        trial_days: parseInt(planForm.trial_days) || 0,
        category: planForm.category,
        is_hidden: planForm.is_hidden,
        features: planForm.features.split('\n').filter(f => f.trim()),
        max_users: planForm.max_users ? parseInt(planForm.max_users) : null,
        max_leads: planForm.max_leads ? parseInt(planForm.max_leads) : null,
        max_customers: planForm.max_customers ? parseInt(planForm.max_customers) : null,
        max_projects: planForm.max_projects ? parseInt(planForm.max_projects) : null,
        custom_branding: planForm.custom_branding,
        api_access: planForm.api_access,
        priority_support: planForm.priority_support,
        badge_text: planForm.badge_text || null,
        sort_order: parseInt(planForm.sort_order) || 0
      };

      if (editingPlan) {
        await axios.put(`${API_URL}/api/plans/${editingPlan.id}`, payload, { headers });
        toast.success('Plan updated successfully!');
      } else {
        await axios.post(`${API_URL}/api/plans/`, payload, { headers });
        toast.success('Plan created successfully!');
      }

      setShowPlanModal(false);
      resetPlanForm();
      fetchPlans();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save plan');
    }
  };

  const deletePlan = async (planId) => {
    if (!window.confirm('Are you sure you want to delete this plan?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/plans/${planId}`, { headers });
      toast.success('Plan deleted successfully!');
      fetchPlans();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete plan');
    }
  };

  const togglePlanActive = async (planId) => {
    try {
      const response = await axios.post(`${API_URL}/api/plans/${planId}/toggle-active`, {}, { headers });
      toast.success(response.data.message);
      fetchPlans();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to toggle plan');
    }
  };

  const openAssignModal = (plan) => {
    setAssigningPlan(plan);
    setAssignForm({
      target_id: '',
      billing_interval: plan.billing_intervals?.[0] || 'monthly',
      start_trial: plan.trial_days > 0
    });
    setShowAssignModal(true);
  };

  const assignPlan = async () => {
    try {
      const params = new URLSearchParams({
        plan_id: assigningPlan.id,
        target_type: assigningPlan.category,
        target_id: assignForm.target_id,
        billing_interval: assignForm.billing_interval,
        start_trial: assignForm.start_trial.toString()
      });

      await axios.post(`${API_URL}/api/plans/assign?${params}`, {}, { headers });
      toast.success('Plan assigned successfully!');
      setShowAssignModal(false);
      fetchSubscriptions();
      fetchBuilders();
      fetchSalesCompanies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign plan');
    }
  };

  const toggleBillingInterval = (interval) => {
    const current = planForm.billing_intervals;
    if (current.includes(interval)) {
      if (current.length > 1) {
        setPlanForm({ ...planForm, billing_intervals: current.filter(i => i !== interval) });
      }
    } else {
      setPlanForm({ ...planForm, billing_intervals: [...current, interval] });
    }
  };

  const builderPlans = plans.filter(p => p.category === 'builder');
  const salesCompanyPlans = plans.filter(p => p.category === 'sales_company');

  const getTargetOptions = () => {
    if (assigningPlan?.category === 'builder') {
      return builders.filter(b => !b.subscription_plan_id);
    }
    return salesCompanies.filter(sc => !sc.subscription_plan_id);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600"></div>
        </div>
      </DashboardLayout>
    );
  }

  const PlanCard = ({ plan }) => (
    <Card className={`relative ${!plan.is_active ? 'opacity-60' : ''} ${plan.is_hidden ? 'border-dashed border-2 border-purple-500' : ''}`}>
      {plan.badge_text && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <Badge className="bg-orange-500 text-white">{plan.badge_text}</Badge>
        </div>
      )}
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-bold text-lg text-gray-900 dark:text-white">{plan.name}</h3>
              {plan.is_hidden && (
                <Badge variant="outline" className="text-purple-600 border-purple-600">
                  <EyeOff className="w-3 h-3 mr-1" />
                  Hidden
                </Badge>
              )}
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{plan.description}</p>
          </div>
          <Badge variant={plan.is_active ? "default" : "secondary"}>
            {plan.is_active ? 'Active' : 'Inactive'}
          </Badge>
        </div>

        {/* Pricing */}
        <div className="mb-4 space-y-1">
          <div className="flex items-baseline gap-1">
            <span className="text-3xl font-bold text-gray-900 dark:text-white">${plan.price_monthly}</span>
            <span className="text-gray-500">/mo</span>
          </div>
          {plan.price_quarterly && (
            <div className="text-sm text-gray-500 dark:text-gray-400">
              ${plan.price_quarterly}/quarter
            </div>
          )}
          {plan.price_yearly && (
            <div className="text-sm text-green-600 dark:text-green-400">
              ${plan.price_yearly}/year (Save {Math.round((1 - plan.price_yearly / (plan.price_monthly * 12)) * 100)}%)
            </div>
          )}
        </div>

        {/* Trial */}
        {plan.trial_days > 0 && (
          <div className="flex items-center gap-2 mb-4 text-blue-600 dark:text-blue-400">
            <Gift className="w-4 h-4" />
            <span className="text-sm font-medium">{plan.trial_days}-day free trial</span>
          </div>
        )}

        {/* Billing Intervals */}
        <div className="flex gap-2 mb-4">
          {plan.billing_intervals?.map(interval => (
            <Badge key={interval} variant="outline" className="text-xs">
              {interval}
            </Badge>
          ))}
        </div>

        {/* Features */}
        {plan.features?.length > 0 && (
          <ul className="space-y-2 mb-4 max-h-32 overflow-y-auto">
            {plan.features.map((feature, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-300">
                <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
        )}

        {/* Limits */}
        <div className="grid grid-cols-2 gap-2 mb-4 text-xs text-gray-500 dark:text-gray-400">
          {plan.max_users && <div>Users: {plan.max_users}</div>}
          {plan.max_leads && <div>Leads: {plan.max_leads}</div>}
          {plan.max_customers && <div>Customers: {plan.max_customers}</div>}
          {plan.max_projects && <div>Projects: {plan.max_projects}</div>}
        </div>

        {/* Perks */}
        <div className="flex flex-wrap gap-2 mb-4">
          {plan.custom_branding && (
            <Badge variant="secondary" className="text-xs">Custom Branding</Badge>
          )}
          {plan.api_access && (
            <Badge variant="secondary" className="text-xs">API Access</Badge>
          )}
          {plan.priority_support && (
            <Badge variant="secondary" className="text-xs">Priority Support</Badge>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-2 pt-4 border-t dark:border-gray-700">
          <Button size="sm" variant="outline" onClick={() => openEditModal(plan)}>
            <Edit className="w-4 h-4 mr-1" />
            Edit
          </Button>
          <Button size="sm" variant="outline" onClick={() => togglePlanActive(plan.id)}>
            {plan.is_active ? <X className="w-4 h-4" /> : <Check className="w-4 h-4" />}
          </Button>
          <Button size="sm" onClick={() => openAssignModal(plan)}>
            <ChevronRight className="w-4 h-4 mr-1" />
            Assign
          </Button>
          <Button size="sm" variant="destructive" onClick={() => deletePlan(plan.id)}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="plan-management-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Subscription Plans
            </h1>
            <p className="text-gray-500 dark:text-gray-400">
              Create and manage subscription plans for Builders and Sales Companies
            </p>
          </div>
          <Button onClick={() => { resetPlanForm(); setShowPlanModal(true); }} data-testid="create-plan-btn">
            <Plus className="w-4 h-4 mr-2" />
            Create Plan
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/50 rounded-lg flex items-center justify-center">
                <Building2 className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Builder Plans</p>
                <p className="text-xl font-bold text-gray-900 dark:text-white">{builderPlans.length}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 bg-green-100 dark:bg-green-900/50 rounded-lg flex items-center justify-center">
                <Store className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Sales Co. Plans</p>
                <p className="text-xl font-bold text-gray-900 dark:text-white">{salesCompanyPlans.length}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/50 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Active Subscriptions</p>
                <p className="text-xl font-bold text-gray-900 dark:text-white">
                  {subscriptions.filter(s => s.status === 'active' || s.status === 'trialing').length}
                </p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900/50 rounded-lg flex items-center justify-center">
                <EyeOff className="w-5 h-5 text-orange-600 dark:text-orange-400" />
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Hidden Plans</p>
                <p className="text-xl font-bold text-gray-900 dark:text-white">
                  {plans.filter(p => p.is_hidden).length}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-gray-100 dark:bg-gray-800">
            <TabsTrigger value="builder" className="data-[state=active]:bg-white dark:data-[state=active]:bg-gray-700">
              <Building2 className="w-4 h-4 mr-2" />
              Builder Plans ({builderPlans.length})
            </TabsTrigger>
            <TabsTrigger value="sales_company" className="data-[state=active]:bg-white dark:data-[state=active]:bg-gray-700">
              <Store className="w-4 h-4 mr-2" />
              Sales Company Plans ({salesCompanyPlans.length})
            </TabsTrigger>
            <TabsTrigger value="subscriptions" className="data-[state=active]:bg-white dark:data-[state=active]:bg-gray-700">
              <CreditCard className="w-4 h-4 mr-2" />
              Active Subscriptions ({subscriptions.length})
            </TabsTrigger>
          </TabsList>

          {/* Builder Plans */}
          <TabsContent value="builder" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {builderPlans.map(plan => (
                <PlanCard key={plan.id} plan={plan} />
              ))}
              {builderPlans.length === 0 && (
                <Card className="col-span-full">
                  <CardContent className="p-12 text-center">
                    <Building2 className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                      No Builder Plans Yet
                    </h3>
                    <p className="text-gray-500 dark:text-gray-400 mb-4">
                      Create your first plan for Builders
                    </p>
                    <Button onClick={() => { resetPlanForm(); setPlanForm(f => ({...f, category: 'builder'})); setShowPlanModal(true); }}>
                      <Plus className="w-4 h-4 mr-2" />
                      Create Builder Plan
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Sales Company Plans */}
          <TabsContent value="sales_company" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {salesCompanyPlans.map(plan => (
                <PlanCard key={plan.id} plan={plan} />
              ))}
              {salesCompanyPlans.length === 0 && (
                <Card className="col-span-full">
                  <CardContent className="p-12 text-center">
                    <Store className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                      No Sales Company Plans Yet
                    </h3>
                    <p className="text-gray-500 dark:text-gray-400 mb-4">
                      Create your first plan for Sales Companies
                    </p>
                    <Button onClick={() => { resetPlanForm(); setPlanForm(f => ({...f, category: 'sales_company'})); setShowPlanModal(true); }}>
                      <Plus className="w-4 h-4 mr-2" />
                      Create Sales Company Plan
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Active Subscriptions */}
          <TabsContent value="subscriptions" className="space-y-4">
            <Card>
              <CardContent className="p-0">
                {subscriptions.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 dark:bg-gray-800">
                        <tr>
                          <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Subscriber</th>
                          <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Plan</th>
                          <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Billing</th>
                          <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Amount</th>
                          <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                          <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Created</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y dark:divide-gray-700">
                        {subscriptions.map(sub => (
                          <tr key={sub.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                {sub.subscriber_type === 'builder' ? (
                                  <Building2 className="w-4 h-4 text-blue-500" />
                                ) : (
                                  <Store className="w-4 h-4 text-green-500" />
                                )}
                                <span className="text-sm font-medium text-gray-900 dark:text-white">
                                  {sub.subscriber_name}
                                </span>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                              {sub.plan_name}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 capitalize">
                              {sub.billing_interval}
                            </td>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                              ${sub.amount?.toFixed(2)}
                            </td>
                            <td className="px-4 py-3">
                              <Badge variant={
                                sub.status === 'active' ? 'default' :
                                sub.status === 'trialing' ? 'secondary' :
                                'destructive'
                              }>
                                {sub.status === 'trialing' && <Gift className="w-3 h-3 mr-1" />}
                                {sub.status}
                              </Badge>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                              {new Date(sub.created_at).toLocaleDateString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="p-12 text-center text-gray-500 dark:text-gray-400">
                    No active subscriptions yet
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Create/Edit Plan Modal */}
      <Dialog open={showPlanModal} onOpenChange={setShowPlanModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPlan ? 'Edit Plan' : 'Create New Plan'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-700 dark:text-gray-200">Plan Name *</Label>
                <Input
                  placeholder="e.g., Starter, Professional"
                  value={planForm.name}
                  onChange={(e) => setPlanForm({ ...planForm, name: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-gray-700 dark:text-gray-200">Category *</Label>
                <Select value={planForm.category} onValueChange={(v) => setPlanForm({ ...planForm, category: v })}>
                  <SelectTrigger className="mt-1 bg-white dark:bg-gray-800 text-gray-900 dark:text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white dark:bg-gray-800">
                    <SelectItem value="builder" className="text-gray-900 dark:text-white">Builder Plan</SelectItem>
                    <SelectItem value="sales_company" className="text-gray-900 dark:text-white">Sales Company Plan</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-gray-700 dark:text-gray-200">Description</Label>
              <Input
                placeholder="Brief description of this plan"
                value={planForm.description}
                onChange={(e) => setPlanForm({ ...planForm, description: e.target.value })}
                className="mt-1"
              />
            </div>

            {/* Pricing */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-gray-700 dark:text-gray-200">Monthly Price ($) *</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="49.99"
                  value={planForm.price_monthly}
                  onChange={(e) => setPlanForm({ ...planForm, price_monthly: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-gray-700 dark:text-gray-200">Quarterly Price ($)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="Auto: 10% off"
                  value={planForm.price_quarterly}
                  onChange={(e) => setPlanForm({ ...planForm, price_quarterly: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-gray-700 dark:text-gray-200">Yearly Price ($)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="Auto: 20% off"
                  value={planForm.price_yearly}
                  onChange={(e) => setPlanForm({ ...planForm, price_yearly: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>

            {/* Billing Intervals */}
            <div>
              <Label className="text-gray-700 dark:text-gray-200">Billing Intervals</Label>
              <div className="flex gap-2 mt-2">
                {['monthly', 'quarterly', 'yearly'].map(interval => (
                  <Button
                    key={interval}
                    type="button"
                    variant={planForm.billing_intervals.includes(interval) ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => toggleBillingInterval(interval)}
                    className="capitalize"
                  >
                    {interval}
                  </Button>
                ))}
              </div>
            </div>

            {/* Trial & Visibility */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-gray-700 dark:text-gray-200">Trial Days</Label>
                <Select value={planForm.trial_days.toString()} onValueChange={(v) => setPlanForm({ ...planForm, trial_days: parseInt(v) })}>
                  <SelectTrigger className="mt-1 bg-white dark:bg-gray-800 text-gray-900 dark:text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white dark:bg-gray-800">
                    <SelectItem value="0" className="text-gray-900 dark:text-white">No Trial</SelectItem>
                    <SelectItem value="7" className="text-gray-900 dark:text-white">7 Days</SelectItem>
                    <SelectItem value="14" className="text-gray-900 dark:text-white">14 Days</SelectItem>
                    <SelectItem value="30" className="text-gray-900 dark:text-white">30 Days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-gray-700 dark:text-gray-200">Badge Text</Label>
                <Input
                  placeholder="e.g., Popular, Best Value"
                  value={planForm.badge_text}
                  onChange={(e) => setPlanForm({ ...planForm, badge_text: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-gray-700 dark:text-gray-200">Sort Order</Label>
                <Input
                  type="number"
                  min="0"
                  placeholder="0"
                  value={planForm.sort_order}
                  onChange={(e) => setPlanForm({ ...planForm, sort_order: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>

            {/* Hidden Plan */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_hidden"
                checked={planForm.is_hidden}
                onChange={(e) => setPlanForm({ ...planForm, is_hidden: e.target.checked })}
                className="rounded"
              />
              <Label htmlFor="is_hidden" className="text-gray-700 dark:text-gray-200 cursor-pointer">
                Hidden Plan (only you can assign it)
              </Label>
            </div>

            {/* Features */}
            <div>
              <Label className="text-gray-700 dark:text-gray-200">Features (one per line)</Label>
              <textarea
                className="w-full mt-1 px-3 py-2 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white dark:border-gray-600"
                rows={4}
                placeholder="Unlimited leads&#10;Custom branding&#10;Priority support"
                value={planForm.features}
                onChange={(e) => setPlanForm({ ...planForm, features: e.target.value })}
              />
            </div>

            {/* Limits */}
            <div className="grid grid-cols-4 gap-4">
              <div>
                <Label className="text-gray-700 dark:text-gray-200 text-sm">Max Users</Label>
                <Input
                  type="number"
                  min="0"
                  placeholder="∞"
                  value={planForm.max_users}
                  onChange={(e) => setPlanForm({ ...planForm, max_users: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-gray-700 dark:text-gray-200 text-sm">Max Leads</Label>
                <Input
                  type="number"
                  min="0"
                  placeholder="∞"
                  value={planForm.max_leads}
                  onChange={(e) => setPlanForm({ ...planForm, max_leads: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-gray-700 dark:text-gray-200 text-sm">Max Customers</Label>
                <Input
                  type="number"
                  min="0"
                  placeholder="∞"
                  value={planForm.max_customers}
                  onChange={(e) => setPlanForm({ ...planForm, max_customers: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-gray-700 dark:text-gray-200 text-sm">Max Projects</Label>
                <Input
                  type="number"
                  min="0"
                  placeholder="∞"
                  value={planForm.max_projects}
                  onChange={(e) => setPlanForm({ ...planForm, max_projects: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>

            {/* Perks */}
            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={planForm.custom_branding}
                  onChange={(e) => setPlanForm({ ...planForm, custom_branding: e.target.checked })}
                  className="rounded"
                />
                <span className="text-sm text-gray-700 dark:text-gray-200">Custom Branding</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={planForm.api_access}
                  onChange={(e) => setPlanForm({ ...planForm, api_access: e.target.checked })}
                  className="rounded"
                />
                <span className="text-sm text-gray-700 dark:text-gray-200">API Access</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={planForm.priority_support}
                  onChange={(e) => setPlanForm({ ...planForm, priority_support: e.target.checked })}
                  className="rounded"
                />
                <span className="text-sm text-gray-700 dark:text-gray-200">Priority Support</span>
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPlanModal(false)}>Cancel</Button>
            <Button onClick={savePlan} disabled={!planForm.name || !planForm.price_monthly}>
              {editingPlan ? 'Update Plan' : 'Create Plan'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Plan Modal */}
      <Dialog open={showAssignModal} onOpenChange={setShowAssignModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Plan: {assigningPlan?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-gray-700 dark:text-gray-200">
                Select {assigningPlan?.category === 'builder' ? 'Builder' : 'Sales Company'}
              </Label>
              <Select value={assignForm.target_id} onValueChange={(v) => setAssignForm({ ...assignForm, target_id: v })}>
                <SelectTrigger className="mt-1 bg-white dark:bg-gray-800 text-gray-900 dark:text-white">
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent className="bg-white dark:bg-gray-800">
                  {getTargetOptions().map(target => (
                    <SelectItem key={target.id} value={target.id} className="text-gray-900 dark:text-white">
                      {target.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-gray-700 dark:text-gray-200">Billing Interval</Label>
              <Select value={assignForm.billing_interval} onValueChange={(v) => setAssignForm({ ...assignForm, billing_interval: v })}>
                <SelectTrigger className="mt-1 bg-white dark:bg-gray-800 text-gray-900 dark:text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white dark:bg-gray-800">
                  {assigningPlan?.billing_intervals?.map(interval => (
                    <SelectItem key={interval} value={interval} className="text-gray-900 dark:text-white capitalize">
                      {interval}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {assigningPlan?.trial_days > 0 && (
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="start_trial"
                  checked={assignForm.start_trial}
                  onChange={(e) => setAssignForm({ ...assignForm, start_trial: e.target.checked })}
                  className="rounded"
                />
                <Label htmlFor="start_trial" className="text-gray-700 dark:text-gray-200 cursor-pointer">
                  Start with {assigningPlan.trial_days}-day free trial
                </Label>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignModal(false)}>Cancel</Button>
            <Button onClick={assignPlan} disabled={!assignForm.target_id}>
              Assign Plan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default PlanManagementPage;
